// Per-install controls a person has over a widget they installed: which of the manifest's
// permissions are actually granted, where it may show, and whether it is paused.
//
// They ride along in the installation's config map under reserved `__` keys, so no schema
// or database change is needed. The widget never sees these keys (stripControlKeys) and the
// effective manifest (applyInstallControls) is what every surface and the frame enforce.
//
// Grants are ALWAYS persisted explicitly (even when everything was ticked), together with the
// list of permissions the person has decided on. A later upload that asks for more therefore
// gets nothing new by default — those permissions show up as "pending your approval" until the
// person allows them under Installed → Configuration.

import type { WidgetManifest, WidgetSurface } from '../../services/myToolsApi';

export const CONTROL_KEYS = {
  permissions: '__permissions',        // JSON array of granted permission strings
  decided: '__permissionsDecided',     // JSON array of every permission the person has seen and decided on
  paused: '__paused',                  // 'true' | absent
  surfaces: '__surfaces',              // comma-separated subset of manifest surfaces; absent = manifest default
} as const;

const ALL_SURFACES: WidgetSurface[] = ['page', 'sidebar', 'tile'];

export interface WidgetInstallControls {
  /** null only for installs made before grants were persisted — treated as "everything the manifest asks for". */
  grantedPermissions: string[] | null;
  /** Permissions the person has explicitly granted or refused; anything the manifest adds later is pending. */
  decidedPermissions: string[] | null;
  paused: boolean;
  /** null = the manifest's surfaces. */
  surfaces: WidgetSurface[] | null;
}

export const DEFAULT_CONTROLS: WidgetInstallControls = { grantedPermissions: null, decidedPermissions: null, paused: false, surfaces: null };

export function isControlKey(key: string): boolean {
  return key.startsWith('__');
}

function parseStringArray(raw: unknown): string[] | null {
  if (typeof raw !== 'string' || !raw.trim()) return null;
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((p): p is string => typeof p === 'string') : null;
  } catch {
    return null;
  }
}

export function readInstallControls(values: Record<string, string> | null | undefined): WidgetInstallControls {
  if (!values) return DEFAULT_CONTROLS;
  const rawSurfaces = values[CONTROL_KEYS.surfaces];
  const surfaces = typeof rawSurfaces === 'string' && rawSurfaces.trim()
    ? rawSurfaces.split(',').map(s => s.trim()).filter((s): s is WidgetSurface => (ALL_SURFACES as string[]).includes(s))
    : null;
  return {
    grantedPermissions: parseStringArray(values[CONTROL_KEYS.permissions]),
    decidedPermissions: parseStringArray(values[CONTROL_KEYS.decided]),
    paused: values[CONTROL_KEYS.paused] === 'true',
    surfaces,
  };
}

/** Merges controls into a config map. Grants are written explicitly; surfaces only when they differ from the manifest. */
export function writeInstallControls(values: Record<string, string>, controls: WidgetInstallControls, manifest: WidgetManifest | null | undefined): Record<string, string> {
  const next: Record<string, string> = { ...values };
  const manifestPermissions = manifest?.permissions ?? [];

  if (controls.grantedPermissions !== null) {
    next[CONTROL_KEYS.permissions] = JSON.stringify(controls.grantedPermissions.filter(p => (manifestPermissions as string[]).includes(p)));
    // Saving from the controls panel means the person has seen the current manifest in full.
    next[CONTROL_KEYS.decided] = JSON.stringify(manifestPermissions);
  } else {
    delete next[CONTROL_KEYS.permissions];
    delete next[CONTROL_KEYS.decided];
  }

  if (controls.paused) next[CONTROL_KEYS.paused] = 'true';
  else delete next[CONTROL_KEYS.paused];

  const manifestSurfaces = manifestDefaultSurfaces(manifest);
  const surfacesChanged = controls.surfaces !== null
    && (controls.surfaces.length !== manifestSurfaces.length || manifestSurfaces.some(s => !controls.surfaces!.includes(s)));
  if (surfacesChanged) next[CONTROL_KEYS.surfaces] = controls.surfaces!.join(',');
  else delete next[CONTROL_KEYS.surfaces];
  return next;
}

/** Only the pause flag, everything else untouched. Resume buttons must use this rather than
 *  writeInstallControls, which also records every current permission as decided — quietly
 *  turning a newer upload's still-pending permission into a refusal the person never saw. */
export function withPaused(values: Record<string, string>, paused: boolean): Record<string, string> {
  const next: Record<string, string> = { ...values };
  if (paused) next[CONTROL_KEYS.paused] = 'true';
  else delete next[CONTROL_KEYS.paused];
  return next;
}

/** The config the widget itself receives — never the reserved control keys. */
export function stripControlKeys(values: Record<string, string> | null | undefined): Record<string, string> {
  if (!values) return {};
  return Object.fromEntries(Object.entries(values).filter(([k]) => !isControlKey(k)));
}

export function manifestDefaultSurfaces(manifest: WidgetManifest | null | undefined): WidgetSurface[] {
  const surfaces = manifest?.surfaces?.filter(s => (ALL_SURFACES as string[]).includes(s)) ?? [];
  return surfaces.length ? surfaces : ['page'];
}

/** The manifest as this install runs it: permissions ∩ granted, surfaces overridden. */
export function applyInstallControls(manifest: WidgetManifest | null | undefined, controls: WidgetInstallControls): WidgetManifest | null {
  if (!manifest) return null;
  const permissions = controls.grantedPermissions === null
    ? manifest.permissions
    : (manifest.permissions ?? []).filter(p => controls.grantedPermissions!.includes(p));
  const surfaces = controls.surfaces === null
    ? manifest.surfaces
    : manifestDefaultSurfaces(manifest).filter(s => controls.surfaces!.includes(s));
  return { ...manifest, permissions, surfaces };
}

/** Permissions the current manifest asks for that the person has never been shown — off until approved. */
export function pendingPermissions(manifest: WidgetManifest | null | undefined, controls: WidgetInstallControls): string[] {
  const decided = controls.decidedPermissions ?? controls.grantedPermissions;
  if (!decided) return [];
  return (manifest?.permissions ?? []).filter(p => !decided.includes(p));
}

/** Grants after an install prompt: exactly the ticked permissions, in manifest order. */
/**
 * Install values with the permission decision always recorded. An install that skipped the prompt
 * (the widget asked for nothing) used to store no grant list at all — and "no list" means "the
 * whole manifest", so a later version's new permissions were silently granted, with no "awaiting
 * your approval" state. Recording the (possibly empty) list makes them pending instead.
 */
export function withRecordedGrants(values: Record<string, string> | undefined, manifest: WidgetManifest | null | undefined): Record<string, string> {
  const base = values ?? {};
  if (!manifest || base[CONTROL_KEYS.permissions] !== undefined) return base;
  return writeInstallControls(base, { ...DEFAULT_CONTROLS, grantedPermissions: [...(manifest.permissions ?? [])] }, manifest);
}

export function grantedFromSelection(manifest: WidgetManifest | null | undefined, selected: Set<string>): string[] {
  return (manifest?.permissions ?? []).filter(p => selected.has(p));
}

/** A preview (marketplace card, review queue) must never exercise permissions the viewer hasn't granted. */
export function previewManifest(manifest: WidgetManifest | null | undefined): WidgetManifest | null {
  return manifest ? { ...manifest, permissions: [] } : null;
}
